/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import type React from 'react';
import { windSpeedRanges } from './windsymbols';
import type { Margin } from '../Soundings/drawUtils';
import type { ScalesType } from '../Soundings/types';

export interface WindDataType {
  pressure: number;
  windSpeed: number;
  windDirection: number;
}

export interface DrawWindBarbsProps {
  svgRef: React.MutableRefObject<SVGSVGElement | null>;
  // Plotting group returned by initializeSvg; animations are applied here
  plotGroup: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  yScale: ScalesType;
  data: WindDataType[];
  width: number;
  margin: Margin;
  barbColor?: string;
  columnOffsetX?: number;
}

const extendIsoBar = (path: string, horizontalBarbMargin: number): string => {
  const match = /^M([\d.-]+),([\d.-]+)\s*L([\d.-]+),([\d.-]+)/.exec(path);
  if (match) {
    const startX = parseFloat(match[1]);
    const startY = parseFloat(match[2]);
    const endX = parseFloat(match[3]);
    const endY = parseFloat(match[4]);
    return `M${startX},${startY}L${endX + horizontalBarbMargin * 3},${endY}`;
  }
  return path;
};

const extendXAxis = (path: string, horizontalBarbMargin: number): string => {
  const match = /^M([\d.-]+),([\d.-]+)H([\d.-]+)/.exec(path);
  if (match) {
    const endX = parseFloat(match[3]);
    return path.replace(/H[\d.-]+/, `H${endX + horizontalBarbMargin * 3}`);
  }
  return path;
};

export const drawWindBarbs = ({
  svgRef,
  plotGroup,
  yScale,
  data,
  width,
  margin,
  barbColor = 'black',
  columnOffsetX = 0,
}: DrawWindBarbsProps): void => {
  const barbsSize = 40;
  const scalingFactor = 0.5;
  const horizontalBarbMargin = barbsSize * scalingFactor + 5;
  const visibleItems = removeItemsExponential(data.reverse()).filter(
    (item) => item.pressure >= 150,
  );
  // Measure width and height and make extra room for one column
  // of windbarbs
  const rootSvg = d3.select(svgRef.current);
  const currentWidth = rootSvg.node()?.getBoundingClientRect().width;
  if (!currentWidth) {
    return;
  }

  // Add extra width to all isobars needed for the windbarbs
  d3.selectAll('path[type="isobar"]').each((_d, i, nodes) => {
    const path = d3.select(nodes[i]).attr('d');
    const newD = extendIsoBar(path, horizontalBarbMargin);
    d3.select(nodes[i]).attr('d', newD).attr('clip-path', null);
  });
  // Add extra width to x-axis needed for the windbarbs
  d3.select('g[type="x-axis"] path.domain').each((_d, i, nodes) => {
    const path = d3.select(nodes[i]).attr('d');
    const newD = extendXAxis(path, horizontalBarbMargin);
    d3.select(nodes[i]).attr('d', newD);
  });

  // Expand root SVG width to make room for barbs column at the right
  rootSvg.attr('width', currentWidth + margin.left + horizontalBarbMargin);

  // Place the barbs just to the right of the plotting area.
  // plotGroup's origin is already translated to (margin.left, margin.top),
  // and its width equals `width`.
  const windBarbColumnX = width + 5 + columnOffsetX;

  const barbGroup = plotGroup.append('g').attr('class', 'wind-group').attr(
    'transform',
    // This translation should be revisited when implementing
    // the plotting of several datasets at once
    `translate(${windBarbColumnX}, 0)`,
  );

  barbGroup
    .selectAll('g')
    .data(visibleItems)
    .enter()
    .append('g')
    .attr('id', 'wind-barb')
    .each(function handleBarb(data: WindDataType) {
      const group = d3.select(this);
      const windSymbol = getWindSymbol(data.windSpeed) as SVGGraphicsElement;
      if (windSymbol) {
        // Inner group will have rotation and scaling applied to it
        const innerGroup = group.append('g');
        innerGroup.node()?.appendChild(windSymbol);

        // Update stroke color
        innerGroup.selectAll('*').attr('stroke', barbColor);
        // Only update the fill for the paths that already used a fill
        // in their original svg form.
        innerGroup.selectAll('*').each((_d, i, nodes) => {
          const el = d3.select(nodes[i]);
          const currentFill = el.attr('fill');
          if (currentFill && currentFill !== 'none') {
            el.attr('fill', barbColor);
          }
        });
        const bbox = windSymbol.getBBox();
        // Central point
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;
        const rotation = 180 + data.windDirection;
        const y = yScale(data.pressure);

        // Place items in the correct vertical position
        // Correcting for barbSize
        group.attr(
          'transform',
          `translate(0, ${y - (barbsSize * scalingFactor) / 8})`,
        );

        // Rotate accordingly, and scale
        innerGroup.attr(
          'transform',
          `rotate(${rotation}) scale(${scalingFactor}) translate(${-cx}, ${-cy})`,
        );
      }
    });
};

export const removeItemsExponential = (
  array: WindDataType[],
): WindDataType[] => {
  const base = 1.15;

  const indicesToKeep: number[] = [];
  let i = 0;
  let nextIndex = 0;

  while (nextIndex < array.length) {
    nextIndex = Math.floor(base ** i);

    indicesToKeep.push(nextIndex);
    i += 1;
  }

  return array.filter((_, index) => indicesToKeep.includes(index));
};

export const getWindSymbol = (value: number): Node | null => {
  const matchedRange = windSpeedRanges.find(
    ({ min, max }) => value >= min && value < max,
  );

  // Out of range
  if (!matchedRange) {
    return null;
  }
  // SVG string to actual SVG element
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(matchedRange.symbol, 'image/svg+xml');
  const svgNode = svgDoc.documentElement;

  // Return the parsed SVG node
  return svgNode;
};
